// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { DataTableCssOverrides } from "@conklin/MobiusOverrides/DataTable/DataTable";
import IconCaretDown from "@conklin/XDL/Icons/IconCaretDown";
import IconCaretUp from "@conklin/XDL/Icons/IconCaretUp";
import IconSortOrderFilled from "@conklin/XDL/Icons/IconSortOrderFilled";
import { fontConfig } from "@conklin/XDL/PreStyle/typography";
import { xBaseTheme } from "@conklin/XDL/types";
import { color, xScreen } from "@conklin/XDL/Utilities";
import { StyledButton } from "@insite/mobius/Clickable";
import DataTablePresentationPropsDefault from "@insite/mobius/DataTable/presentationProps";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { css } from "styled-components";

const stickyMode = () => css`
    ${xScreen("lte", "sm")} {
        position: sticky;
        left: -5px;
        z-index: 2;
        background-clip: padding-box;
        background: inherit;
    }
`;

export default {
    defaultProps: {
        ...DataTablePresentationPropsDefault,
        mergeCss: true,
        cssOverrides: {
            header: css`
                background: ${color("common.ui6")};
                padding: 6px 8px;
                border-bottom: 0;
                height: initial;
                line-height: 1;
            `,
            head: css`
                background: ${color("common.ui6")};
                th:first-child {
                    ${stickyMode()}
                }
            `,
            cell: css`
                padding: 8px;
                border-bottom: 0;
                height: initial;
                line-height: 1;
                &:first-child {
                    ${stickyMode()}
                }
                > ${StyledButton} {
                    &,
                    & ${TypographyStyle} {
                        font-size: 14px;
                        line-height: 22px;
                        font-weight: ${fontConfig.weight.bold};
                        width: 100%;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }
                }
            `,
            row: css`
                background: ${color("common.white")};
            `,
            evenRow: css`
                background: ${color("common.ui2")};
            `,
        } as DataTableCssOverrides,
        headerTypographyProps: {
            mergeCss: true,
            size: 12,
            weight: fontConfig.weight.bold,
            color: "common.white",
            css: css`
                letter-spacing: 0;
                line-height: 15px;
                display: block;
            `,
        },
        cellTypographyProps: {
            mergeCss: true,
            size: 14,
            weight: fontConfig.weight.regular,
            css: css`
                letter-spacing: 0;
                line-height: 22px;
            `,
        },
        sortClickableProps: {
            css: css`
                width: 100%;
                height: 100%;
            `,
        },
        sortIconProps: {
            size: 15,
            color: "common.ui4",
            css: css`
                margin-left: auto;
                & > svg {
                    padding-left: 8px;
                }
            `,
        },
        sortIconSources: {
            sortable: IconSortOrderFilled,
            ascending: IconCaretUp,
            descending: IconCaretDown,
            none: "Minus",
            other: "Activity",
        },
    },
} as xBaseTheme["dataTable"];
